/* This is a React component that renders a form wizard for an annual grant application with various
fields and validations. */
import React from 'react'

/* These are import statements that bring in various modules and constants needed for the
implementation of the React component. */

import Wizard from '@modules/form-wizard'
import SERVICE_PROPS from '@helpers/extract-service-props'
import daysOfWeek from '@constants/weekdys'
import moment from 'moment'
import { commonBanks } from '@constants/banking-institutions'

const today = new Date().toISOString().slice(0, 10)

/* This is a React component that exports a default class named `__SERVICE_NAME__`. The component
contains various properties and methods that define the behavior and appearance of a form wizard for
an annual grant application. */
export default class __SERVICE_NAME__ extends React.Component {
  resource = '/annual_grants'
  form_key = 'annual_grant'
  defaults = {
    shareholders_list: [
      {
        position: 'President',
        first_name: '',
        last_name: '',
        po_box: '',
        email: '',
        phone_numb: '',
        place_of_employment: '',
      },
      {
        position: 'Vice President',
        first_name: '',
        last_name: '',
        po_box: '',
        email: '',
        phone_numb: '',
        place_of_employment: '',
      },
      {
        position: 'Secretary',
        first_name: '',
        last_name: '',
        po_box: '',
        email: '',
        phone_numb: '',
        place_of_employment: '',
      },
      {
        position: 'Treasurer',
        first_name: '',
        last_name: '',
        po_box: '',
        email: '',
        phone_numb: '',
        place_of_employment: '',
      },
    ]
  }
/* These are properties and methods defined within the `__SERVICE_NAME__` React component. */

  free           = true
  skip_location  = true
  hide_buttons   = false
  custom_actions = []

  validate = null
  foremost = null

  fields = form => [
    '::Organization Details',
    {
      name: 'organization_name',
      label: 'Name of Organization',
    },
    {
      name: 'project_name',
      label: 'Name of Project/Event',
      hint: 'If same as organization, please state the name of the organization',
    },
    {
      name: 'position_in_organization',
      label: 'What is your position in the organization?',
      options: [
        'President',
        'Vice President',
        'Secretary',
        'Treasurer',
        'Other'
      ]
    },
    {
      name: 'position_in_organization_other',
      label: 'Other Position in Organization',
      hide: form.position_in_organization != 'Other',
    },
    {
      name: 'street_address_organization',
      label: 'Full Street Address of physical location of organization',
    },
    {
      name: 'street_address_event',
      label: 'Full Street Address of event',
      required: false,
    },
    {
      name: 'phone_numb_organization',
      label: 'Phone Number for Organization',
      type: 'phone',
    },

    '::Social Media',
    {
      name: 'current_website',
      label: 'Do you have a current website/social media pages?',
      type: 'select:bool'
    },
    {
      name: 'web_address',
      label: 'Website URL',
      hint: 'Please provide the URL, eg: https://www.example.com',
      hide: !form.current_website,
      required: false,
    },
    {
      name: 'social_media_list',
      label: 'Please provide them',
      fields: [
        { 
          name: 'social_media',
          label: 'Social Media Platform',
          options: [
            'Facebook',
            'Instagram',
            'Twitter',
            'LinkedIn',
            'Snapchat',
            'YouTube',
            'TikTok',
          ]
        },
        {
          name: 'account_name',
          label: 'Account Name',
          hint: 'Please provide the account name associated with social media page'
        },
      ],
      hide: !form.current_website
    },

    '::Organization Details (cont.)',
    {
      name: 'vision_statement',
      label: 'Vision Statement',
      type: 'textarea',
    },
    {
      name: 'mission_statement',
      label: 'Mission Statement',
      type: 'textarea',
    },
    {
      name: 'project_outline_list',
      label: 'Project Outline',
      fields: [
        { name: 'objective', label: 'Objective' },
        {
          name: 'requesting_funding',
          label: 'Requesting Funding',
          type: 'select:bool'
        },

      ],
    },
    {
      name: 'funding_request',
      label: 'Is funding requested for general programme facilitation?',
      type: 'select:bool',
    },
    {
      name: 'steps_to_success',
      type: 'textarea',
      label: 'Describe how you intend to meet your objectives',
    },
    {
      name: 'target_audience',
      label: 'Specific Target Population of this organization',
    },
    {
      name: 'meeting_info_list',
      label: 'Meeting Dates & Times',
      fields: [
        {
          name: 'day',
          options: daysOfWeek,
        },
        {
          name: 'time',
          type: 'time',
        },
        'location'
      ]
    },
    {
      name: 'youth_issued_addressed_list',
      label: 'Youth Issues Addressed by this organization',
      fields: [
        {
          name: 'issue',
          options: [
            'Literacy',
            'Crime & Violence Reduction',
            'Career Development, Employment, & Entrepeneurship Skill Development',
            'Responsible Sexual Behaviour',
            'Youth Advocacy & Empowerment',
            'Environmental Education',
            'Substance Abuse Reduction',
            'Promotion of Volunteerism'
          ]
        },
        {
          label: 'How Was It Addressed?',
          name: 'how_was_addressed',
          type: 'textarea',
        }
      ]
    },

    '::Organization Heads (Executive Team)',
    {
      name: 'shareholders',
      label: 'Executive Team',
      min: 4,
      max: 4,
      fields: [
        {
          name: 'position',
          label: 'Position',
          options: [
            'President',
            'Vice President',
            'Secretary',
            'Treasurer',
          ]
        },
        {
          name: 'first_name',
          label: 'First Name',
        },
        {
          name: 'middle_name',
          label: 'Middle Name',
          required: false,
        },
        {
          name: 'last_name',
          label: 'Last Name',
        },
        {
          name: 'email',
          type: 'email'
        },
        {
          name: 'phone_number',
          type: 'phone',
          required: false,
        },
        { 
          name: 'place_of_employment',
          label: 'Regular place of employment'
        },
        {
          name: 'po_box',
          label: 'P. O. Box',
          required: false,
        },

      ]
    },

    {
      name: 'expected_outcomes_list',
      label: 'What are your expected outcomes?',
      hint: `List specific measurable changes in your 
        target population that you expect to see as a result of participation in your 
        programme.`,
      columns: ['outcome'],
      format: v => v.outcome
    },
    {
      name: 'typical_session',
      label: 'Describe a typical session with your group',
    },
    {
      name: 'successful_events_list',
      label: `List and describe 3 successful events that your organization has hosted or 
        participated in, in the past 18 months.`,
      fields: ['event'],
      // format: v => v.event
    },

    '::Financial Matters',
    {
      name: 'grant_amount_requested',
      label: `Detail the amount of the grant requested`,
      type: 'currency',
    },
    {
      name: 'grant_usage_details',
      label: 'Details of how the funds will be used',
      type: 'textarea',
    },
    {
      name: 'grant_needed_by',
      label: `For which calendar year are the funds to be used? If funds are requested for a 
        specific event, please list date of event. State by when you need to receive the 
        funds.`,
      type: 'date',
      minDate: today,
      showYearDropdown: true,
    },
    {
      name: 'grant_needed_by_event_list',
      label: 'Event Date',
      fields: [
        {
          name: 'event',
          label: 'Event Description',
        },
        {
          name: 'date',
          label: 'Date of event',
          type: 'date',
          minDate: today,
          showYearDropdown: true,
        },
        {
          name: 'grant_needed_by',
          label: 'When do you need to receive the funds?',
          type: 'date',
          minDate: today,
          showYearDropdown: true,
        },
      ],
      required: false,
    },
    {
      name: 'fund_sources',
      label: 'Other Source(s) of Funding',
      hint: `Indicate other sources of funding/fund raising initiatives and the respective 
        amounts received.`,
      fields: [
        'source',
        {
          name: 'amount_received',
          type: 'currency',
        }
      ]
    },
    {
      name: 'funds_granted_previous_year',
      label: 'Did you receive a grant last year?',
      type: 'select:bool',
    },
    {
      name: 'funds_granted_amount',
      label: 'Grant Amount Rewarded',
      type: 'currency',
      hide: !form.funds_granted_previous_year,
    },

    '::Banking Info',
    {
      name: 'bank_name',
      label: 'Name of Bank',
      options: commonBanks,
    },
    {
      name: 'branch_numb',
      label: 'Branch #',
    },
    {
      name: 'branch_address',
      label: 'Branch Address',
    },
    {
      name: 'name_on_bank_account',
      label: 'Name on Bank Acct.',
    },
    {
      name: 'bank_account_numb',
      label: 'Acct. #',
    },
    {
      name: 'bank_account_type',
      label: 'Type of Account',
      options: [
        'Savings',
        'Chequing',
        'Business Chequings',
        'Charge Account'
      ]
    },

    '::Compliance',
    {
      name: 'training_info_list',
      label: ``,
      fields: [
        { name: 'first_name', label: 'First Name' },
        { name: 'middle_name', label: 'Middle Name', required: false },
        { name: 'last_name', label: 'Last Name' },
        {
          name: 'qualification',
          label: 'Qualification',
          options: [
            'Youth Leaders Certification',
            'Safe Guarding',
            'Mental Health First Responder',
            'First Aid/CPR',
          ]
        },
        {
          name: 'other_qualification',
          label: 'Other Qualification',
          hint: 'Please include Institution/Programme name',
        },
        {
          name: 'date_completed',
          label: 'Date completed',
          type: 'date',
          showYearDropdown: true
        },
      ],
    },

    '::Certification',
    {
      heading: `On behalf of <b>${form.organization_name}</b>, we apply for a grant in the amount of <b>$${form.grant_amount_requested}</b>
        in respect of expenditure to be incurred in the financial year ending <b>${moment(form.grant_needed_by).format('MMMM Do YYYY')}</b> for the programme/event specified.
        I understand that all information herein is subject to further investigation by (an) 
        official(s) within the Division of Youth. I certify that to the best of our knowledge, 
        the statements made by me in this application are true and that the information 
        provided is correct. I also understand that submitting an application does not guarantee
        approval of this grant request.`,
    },

    '::Uploads',
    {
      key: 'image.0',
      name: 'vendor_form_upload',
      type: 'file:all',
      label: 'Vendor Verification Form',
    },
    {
      key: 'image.1',
      name: 'bank_info_upload',
      type: 'file:all',
      label: 'Bank Info',
    },
    {
      key: 'image.2',
      name: 'business_nib_upload',
      type: 'file:all',
      label: 'Business NIB',
    },
    {
      key: 'image.3',
      name: 'cash_flow_report_upload',
      type: 'file:all',
      label: 'Cash Flow Report',
    },
    {
      key: 'image.4',
      name: 'tax_comp_cert_upload',
      type: 'file:all',
      label: 'Tax Compliance Cert',
    },
    {
      key: 'image.5',
      name: 'business_license_upload',
      type: 'file:all',
      label: 'Business Licence',
      required: !form.non_profit_cert_upload,
    },
    {
      key: 'image.6',
      name: 'non_profit_cert_upload',
      type: 'file:all',
      label: 'Certificate for Non-Profit Organizations',
      required: !form.business_license_upload,
    },
    {
      key: 'image.7',
      name: 'quote_upload',
      type: 'file:all',
      label: 'Quotations for materials/items/services listed',
    },
  ]

  render = () => <Wizard {...SERVICE_PROPS(this)} />
}
